import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import UtmGeometryPreview from '@/components/utm/shared/UtmGeometryPreview';

type FlightPlanResponseValue = {
  geometry?: GeoJSON.Geometry;
  selectionMode?: 'polygon' | 'radius' | 'corridor';
};

interface FlightPlanResponseProps {
  response: FlightPlanResponseValue | null;
  label: string;
}

const FlightPlanResponse: React.FC<FlightPlanResponseProps> = ({ response }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const geometry = useMemo(() => {
    if (!response || typeof response !== 'object') {
      return null;
    }

    if (!response.geometry) {
      return null;
    }

    return response.geometry;
  }, [response]);

  if (!geometry) {
    return (
      <div className="main-response-container text-slate-500 text-sm">
        {t('formResponses.noFlightPlan')}
      </div>
    );
  }

  const handleOpenUtmMap = () => {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;

    navigate('/admin/uzamaps/maps', {
      state: {
        flightPlanSelection: {
          geometry,
          selectionMode: response?.selectionMode,
        },
        returnTo,
      },
    });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="px-2 py-1 rounded text-xs border bg-darkBlue text-white border-darkBlue"
        >
          {t('formResponses.previewMode')}
        </button>
        <button
          type="button"
          onClick={handleOpenUtmMap}
          className="px-2 py-1 rounded text-xs border bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
        >
          {t('formResponses.utmMapMode')}
        </button>
      </div>

      <UtmGeometryPreview geometry={geometry} height="430px" />

      <div className="text-xs text-slate-500">
        {t('formResponses.geometryType')}: {geometry.type}
      </div>
    </div>
  );
};

export default FlightPlanResponse;
